import * as Sentry from '@sentry/nuxt'

// @sentry/nuxt 以 `vue:error` hook 上報 Vue 崩潰，但其 reportNuxtError 只帶 info 字串（上游原始碼標註
// `todo: add component name and trace`），且刻意以 attachErrorHandler: false 關閉 @sentry/vue 自己的
// error handler，因此 vue-error 事件既無第一方 frame 也無元件資訊 —— CLIENT-PWA-SSR-C1~C5 這類 runtime
// patch/unmount 崩潰的 stacktrace 全在 @vue/runtime-core 內，無法定位到出錯元件（issue #322）。
// 補強方式：於 vue:error 取得 instance 後建出元件鏈，以 error 物件為 key 暫存於 WeakMap，再由 event
// processor 依 hint.originalException 對回同一筆事件。SDK 的 captureException 包在 setTimeout 內，
// 必定晚於同步執行的本 hook，對應關係成立；WeakMap 不持有 error 的強參考，未被上報者隨 error 一起回收。
const MAX_COMPONENT_TRACE_DEPTH = 10
const ANONYMOUS_COMPONENT_NAME = 'Anonymous'
const ROOT_COMPONENT_NAME = 'Root'
const TRUNCATED_TRACE_MARKER = '…'

const formatComponentName = (vm) => {
  if (vm.$root === vm) return ROOT_COMPONENT_NAME

  // 正式 build 只保留 SFC 編譯注入的 __name（__file 為 dev-only）
  return vm.$options?.name || vm.$options?.__name || ANONYMOUS_COMPONENT_NAME
}

const buildComponentTrace = (instance) => {
  const trace = []
  let vm = instance

  while (vm && trace.length < MAX_COMPONENT_TRACE_DEPTH) {
    trace.push(formatComponentName(vm))
    vm = vm.$parent
  }

  // 未走到 Root 就到深度上限時標示截斷，避免把短鏈誤讀為完整元件鏈
  if (vm) trace.push(TRUNCATED_TRACE_MARKER)

  return trace
}

export default defineNuxtPlugin((nuxtApp) => {
  const componentTraceByError = new WeakMap()

  nuxtApp.hook('vue:error', (error, instance) => {
    if (!instance || !error || typeof error !== 'object') return

    try {
      componentTraceByError.set(error, buildComponentTrace(instance))
    } catch {
      // 元件鏈屬加值診斷資訊，取不到時維持 SDK 原本的上報內容
    }
  })

  Sentry.getGlobalScope().addEventProcessor((event, hint) => {
    try {
      const originalException = hint?.originalException
      const componentTrace = originalException && typeof originalException === 'object' ? componentTraceByError.get(originalException) : null
      if (!componentTrace?.length) return event

      event.contexts = {
        ...event.contexts,
        vue: { ...event.contexts?.vue, componentName: componentTrace[0], trace: componentTrace.join(' < ') },
      }
      // context 無法被 Sentry 搜尋，元件名稱另存一份 tag 以便直接依元件查詢與分群；
      // 葉節點未具名時取最近的具名祖先，避免 tag 退化成無鑑別度的 Anonymous
      const namedComponent = componentTrace.find((name) => name !== ANONYMOUS_COMPONENT_NAME && name !== TRUNCATED_TRACE_MARKER)
      event.tags = { ...event.tags, vueComponent: namedComponent || ANONYMOUS_COMPONENT_NAME }
    } catch {
      // 加值失敗不得影響原始事件送出
    }

    return event
  })

  return {
    provide: {
      sentry: Sentry,
    },
  }
})
